import React, { useEffect, useState } from 'react';
import { getAdminStats, getSuperAdminStats, getUnpaidDetails } from '../api/api';
import { 
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import styles from './AdminDashboard.module.css';

const formatCurrency = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val);
const formatPercent = (val) => `${(val || 0).toFixed(2)}%`;

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState('ops');
    const [selectedManager, setSelectedManager] = useState('');
    const [managersList, setManagersList] = useState([]);
    const [year, setYear] = useState(new Date().getFullYear());
    
    const [opsStats, setOpsStats] = useState(null);
    const [finStats, setFinStats] = useState(null);
    
    const [loadingOps, setLoadingOps] = useState(true);
    const [loadingFin, setLoadingFin] = useState(false);
    const [error, setError] = useState(null);

    const [isUnpaidModalOpen, setIsUnpaidModalOpen] = useState(false);
    const [unpaidData, setUnpaidData] = useState({ summary: {}, details: [] });
    const [loadingUnpaid, setLoadingUnpaid] = useState(false);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState(null); // <-- NOUVEAU

    // --- GESTION DU TRI ET FILTRAGE DE LA MODALE ---
    const [sortConfig, setSortConfig] = useState({ key: 'jours_retard', direction: 'descending' });

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const filteredAndSortedDetails = React.useMemo(() => {
        // 1. Filtrage par statut (si un filtre est actif)
        let items = unpaidData.details;
        if (selectedStatusFilter) {
            items = items.filter(inv => inv.statut === selectedStatusFilter);
        }

        // 2. Tri
        let sortableItems = [...items];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                
                if (aVal === null || aVal === undefined) aVal = '';
                if (bVal === null || bVal === undefined) bVal = '';

                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    return sortConfig.direction === 'ascending' 
                        ? aVal.localeCompare(bVal) 
                        : bVal.localeCompare(aVal);
                }
                
                if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [unpaidData.details, sortConfig, selectedStatusFilter]); // <-- Ne pas oublier d'ajouter selectedStatusFilter aux dépendances
    // -----------------------------------

    const openUnpaidDetails = async () => {
        setIsUnpaidModalOpen(true);
        setLoadingUnpaid(true);
        setSelectedStatusFilter(null); // <-- NOUVEAU : Réinitialise le filtre à l'ouverture
        try {
            const res = await getUnpaidDetails(year, selectedManager);
            setUnpaidData(res.data);
        } catch (err) {
            console.error("Erreur lors de la récupération des impayés", err);
        } finally {
            setLoadingUnpaid(false);
        }
    };

    useEffect(() => {
        const fetchOps = async () => {
            setLoadingOps(true);
            try {
                const res = await getAdminStats(selectedManager);
                setOpsStats(res.data);
                if (managersList.length === 0 && res.data.managers_list) {
                    setManagersList(res.data.managers_list);
                }
            } catch (err) {
                console.error(err);
                setError("Erreur lors du chargement des opérations.");
            } finally {
                setLoadingOps(false);
            }
        };
        fetchOps();
    }, [selectedManager]);

    useEffect(() => {
        if (activeTab === 'finance') {
            const fetchFin = async () => {
                setLoadingFin(true);
                try {
                    const res = await getSuperAdminStats(year, selectedManager);
                    setFinStats(res.data);
                } catch (err) {
                    console.error(err);
                    if (err.response && err.response.status === 403) {
                        setError("Accès refusé aux données financières. Droits SuperAdmin requis.");
                    } else {
                        setError("Erreur lors du chargement des finances.");
                    }
                } finally {
                    setLoadingFin(false);
                }
            };
            fetchFin();
        }
    }, [activeTab, year, selectedManager]);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className={styles.customTooltip}>
                    <p className={styles.tooltipLabel}>{`Mois : ${label}`}</p>
                    <hr />
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color, display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                            <span>{entry.name} :</span>
                            <strong>
                                {entry.name.includes('Taux') 
                                    ? formatPercent(entry.value) 
                                    : formatCurrency(entry.value)}
                            </strong>
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    if (loadingOps && !opsStats) {
        return (
            <div className={styles.dashboardContainer}>
                <div className={styles.loader}>Chargement du tableau de bord...</div>
            </div>
        );
    }

    return (
        <div className={styles.dashboardContainer}>
            <div className={styles.topBar}>
                <div className={styles.titleSection}>
                    <h1>Pilotage PortaLink</h1>
                    <div className={styles.tabsMenu}>
                        <button 
                            className={activeTab === 'ops' ? styles.activeTab : ''} 
                            onClick={() => setActiveTab('ops')}
                        >
                            Opérations
                        </button>
                        <button 
                            className={activeTab === 'finance' ? styles.activeTab : ''} 
                            onClick={() => setActiveTab('finance')}
                        >
                            Finance & Rentabilité
                        </button>
                    </div>
                </div>

                <div className={styles.filterGroup}>
                    <select 
                        value={selectedManager} 
                        onChange={(e) => setSelectedManager(e.target.value)} 
                        className={styles.modernSelect}
                    >
                        <option value="">Tous les managers</option>
                        {managersList.map((m, i) => (
                            <option key={i} value={m}>{m}</option>
                        ))}
                    </select>

                    {activeTab === 'finance' && (
                        <select 
                            value={year} 
                            onChange={(e) => setYear(e.target.value)} 
                            className={styles.modernSelect}
                        >
                            <option value="2024">Exercice 2024</option>
                            <option value="2025">Exercice 2025</option>
                            <option value="2026">Exercice 2026</option>
                        </select>
                    )}
                </div>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {activeTab === 'ops' && opsStats && (
                <div className={styles.fadeIn}>
                    <div className={styles.kpiGrid}>
                        <div className={styles.kpiCard}>
                            <h3>Partenaires Actifs</h3>
                            <p className={styles.kpiValue}>{opsStats.kpi.total_clients}</p>
                        </div>
                        <div className={styles.kpiCard}>
                            <h3>Missions en cours</h3>
                            <p className={styles.kpiValue}>{opsStats.kpi.active_missions}</p>
                        </div>
                        <div className={styles.kpiCard}>
                            <h3>Consultants Actifs</h3>
                            <p className={styles.kpiValue}>{opsStats.kpi.active_consultants}</p>
                        </div>
                        <div className={styles.kpiCard} style={{ borderLeftColor: '#f39c12' }}>
                            <h3>TJM Moyen</h3>
                            <p className={styles.kpiValue} style={{ color: '#f39c12' }}>
                                {formatCurrency(opsStats.kpi.avg_tjm)}
                            </p>
                        </div>
                    </div>

                    <div className={styles.tablesContainer}>
                        <div className={styles.tableSection}>
                            <h2 className={styles.sectionTitle}>Derniers Arrivés</h2>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Nom du consultant</th>
                                        <th>Titre / Rôle</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {opsStats.last_resources.map((res) => (
                                        <tr key={res.resource_id}>
                                            <td style={{ fontWeight: '500' }}>{res.firstName} {res.lastName}</td>
                                            <td>{res.title || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.tableSection}>
                            <h2 className={styles.sectionTitle}>Clients Actifs Récents</h2>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Entreprise partenaire</th>
                                        <th>Contact principal</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {opsStats.last_clients.map((cli) => (
                                        <tr key={cli.client_id}>
                                            <td style={{ fontWeight: '500' }}>{cli.clientName}</td>
                                            <td>{cli.contactName || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'finance' && loadingFin && (
                <div className={styles.loader}>Calcul des données financières en cours...</div>
            )}

            {activeTab === 'finance' && finStats && !loadingFin && (
                <div className={styles.fadeIn}>
                    <div className={styles.kpiGrid} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                        <div className={`${styles.glassCard} ${styles.cardBlue}`}>
                            <span>Volume d'Affaires</span>
                            <h2>{formatCurrency(finStats.kpi.total_revenue)}</h2>
                            <small>CA Total facturé</small>
                        </div>
                        <div className={`${styles.glassCard} ${styles.cardGreen}`}>
                            <span>Marge Entreprise</span>
                            <h2 style={{ color: '#10b981' }}>{formatCurrency(finStats.kpi.total_company_margin)}</h2>
                            <small>Rentabilité brute ({finStats.kpi.margin_percent}%)</small>
                        </div>
                        <div className={`${styles.glassCard} ${styles.cardRed}`}>
                            <span>Charges Payées</span>
                            <h2 style={{ color: '#ef4444' }}>{formatCurrency(finStats.kpi.total_costs)}</h2>
                            <small>Salaires, Virements & NDF</small>
                        </div>
                        <div className={`${styles.glassCard} ${styles.cardOrange}`}>
                            <span>Cagnotte Globale</span>
                            <h2 style={{ color: '#f59e0b' }}>{formatCurrency(finStats.kpi.total_reserve)}</h2>
                            <small>Réserve disponible consultants</small>
                        </div>
                        <div className={styles.glassCard} style={{ borderTop: '4px solid #8e44ad' }}>
                            <span>Taux Moyen Appliqué</span>
                            <h2 style={{ color: '#8e44ad' }}>{formatPercent(finStats.kpi.avg_yearly_rate)}</h2>
                            <small>Sur les factures émises</small>
                        </div>
                        <div 
                            className={`${styles.glassCard} ${styles.cardYellow} ${styles.clickableCard}`}
                            onClick={openUnpaidDetails}
                        >
                            <span>Factures en Attente</span>
                            <h2 style={{ color: '#eab308' }}>{formatCurrency(finStats.kpi.total_unpaid)}</h2>
                            <small>Transmis & Relances (Voir détails)</small>
                        </div>
                    </div>

                    <div className={styles.mainChartCard}>
                        <div className={styles.cardHeader}>
                            <h3>Évolution Financière Mensuelle ({year})</h3>
                        </div>
                        <div style={{ width: '100%', height: 450 }}>
                            <ResponsiveContainer>
                                <ComposedChart data={finStats.chart_data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                    <XAxis 
                                        dataKey="mois" 
                                        tickFormatter={(m) => ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Aoû','Sep','Oct','Nov','Déc'][m-1]} 
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis 
                                        yAxisId="left" 
                                        tickFormatter={(v) => `${v / 1000}k€`} 
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis 
                                        yAxisId="right" 
                                        orientation="right" 
                                        tickFormatter={(v) => `${v}%`} 
                                        domain={[0, 'auto']} 
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ paddingTop: '20px' }} />

                                    <Bar yAxisId="left" dataKey="company_margin" name="Marge Entreprise" stackId="a" fill="#10b981" radius={[0, 0, 4, 4]} />
                                    <Bar yAxisId="left" dataKey="salaries" name="Salaires & RH" stackId="a" fill="#ef4444" />
                                    <Bar yAxisId="left" dataKey="expenses" name="Notes de Frais" stackId="a" fill="#f97316" />
                                    <Bar yAxisId="left" dataKey="consultant_reserve" name="Cagnotte" stackId="a" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                                    
                                    <Line 
                                        yAxisId="left" 
                                        type="monotone" 
                                        dataKey="revenue" 
                                        name="CA Total" 
                                        stroke="#1e293b" 
                                        strokeWidth={3} 
                                        dot={{ stroke: '#1e293b', strokeWidth: 2, r: 4, fill: 'white' }} 
                                    />
                                    <Line 
                                        yAxisId="right" 
                                        type="monotone" 
                                        dataKey="avg_rate" 
                                        name="Taux Moyen" 
                                        stroke="#8e44ad" 
                                        strokeWidth={3} 
                                        dot={{ stroke: '#8e44ad', strokeWidth: 2, r: 4, fill: 'white' }} 
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className={styles.bottomGrid}>
                        <div className={styles.listCard}>
                            <h3>🏆 Top 5 Partenaires (Rentabilité)</h3>
                            <div className={styles.rankingList}>
                                {finStats.top_clients.map((c, i) => (
                                    <div key={i} className={styles.rankingItem}>
                                        <div className={styles.rankBadge}>{i + 1}</div>
                                        <div className={styles.rankInfo}>
                                            <strong>{c.clientName}</strong>
                                            <span>Volume d'affaires : {formatCurrency(c.total_ca)}</span>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div className={styles.rankValue}>
                                                Marge: {formatCurrency(c.total_margin)}
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: '#64748b', margin: '4px 0 0 0' }}>
                                                {((c.total_margin / c.total_ca) * 100).toFixed(2)}% de renta.
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {finStats.top_clients.length === 0 && (
                                    <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>
                                        Aucune donnée de rentabilité pour cette période.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className={styles.listCard}>
                            <h3>💡 Notes de calcul</h3>
                            <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                L'ensemble des données affichées sur ce tableau de bord se base uniquement sur le chiffre d'affaires <strong>réellement facturé</strong>.
                            </p>
                            <div className={styles.infoBox}>
                                <strong>Hiérarchie des taux de gestion appliqués :</strong>
                                <ol style={{ paddingLeft: '20px', marginTop: '10px', marginBottom: 0 }}>
                                    <li>Taux spécifique paramétré sur la mission (Prioritaire)</li>
                                    <li>Historique de la ressource (Mois N)</li>
                                    <li>Taux de gestion par défaut</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isUnpaidModalOpen && (
                <div className={styles.modalOverlay} onClick={() => setIsUnpaidModalOpen(false)}>
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>Détails des factures en attente</h2>
                            <button onClick={() => setIsUnpaidModalOpen(false)} className={styles.closeBtn}>&times;</button>
                        </div>
                        
                        {loadingUnpaid ? (
                            <div className={styles.loader}>Chargement des détails...</div>
                        ) : (
                            <div className={styles.modalBody}>
                                <div className={styles.summaryGrid}>
                                    <div 
                                        className={styles.summaryBox}
                                        style={{ 
                                            cursor: 'pointer', 
                                            transition: 'opacity 0.2s',
                                            /* On vérifie par rapport à 'Transmis' */
                                            opacity: selectedStatusFilter && selectedStatusFilter !== 'Transmis' ? 0.4 : 1 
                                        }}
                                        /* On applique 'Transmis' au filtre */
                                        onClick={() => setSelectedStatusFilter(prev => prev === 'Transmis' ? null : 'Transmis')}
                                    >
                                        <span>Transmis au client</span>
                                        {/* On va chercher la clé 'Transmis' */}
                                        <strong>{formatCurrency(unpaidData.summary['Transmis'] || 0)}</strong>
                                    </div>
                                    <div 
                                        className={styles.summaryBox} 
                                        style={{ 
                                            borderColor: '#f97316', backgroundColor: '#fff7ed', color: '#c2410c',
                                            cursor: 'pointer', transition: 'opacity 0.2s',
                                            opacity: selectedStatusFilter && selectedStatusFilter !== 'Relance 1' ? 0.4 : 1 
                                        }}
                                        onClick={() => setSelectedStatusFilter(prev => prev === 'Relance 1' ? null : 'Relance 1')}
                                    >
                                        <span>Relance 1</span>
                                        <strong>{formatCurrency(unpaidData.summary['Relance 1'] || 0)}</strong>
                                    </div>
                                    <div 
                                        className={styles.summaryBox} 
                                        style={{ 
                                            borderColor: '#ef4444', backgroundColor: '#fef2f2', color: '#b91c1c',
                                            cursor: 'pointer', transition: 'opacity 0.2s',
                                            opacity: selectedStatusFilter && selectedStatusFilter !== 'Relance 2' ? 0.4 : 1 
                                        }}
                                        onClick={() => setSelectedStatusFilter(prev => prev === 'Relance 2' ? null : 'Relance 2')}
                                    >
                                        <span>Relance 2</span>
                                        <strong>{formatCurrency(unpaidData.summary['Relance 2'] || 0)}</strong>
                                    </div>
                                </div>

                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th onClick={() => requestSort('client')} style={{cursor: 'pointer', userSelect: 'none'}}>
                                                Client {sortConfig.key === 'client' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                            </th>
                                            <th onClick={() => requestSort('consultant')} style={{cursor: 'pointer', userSelect: 'none'}}>
                                                Consultant {sortConfig.key === 'consultant' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                            </th>
                                            <th onClick={() => requestSort('manager')} style={{cursor: 'pointer', userSelect: 'none'}}>
                                                Manager {sortConfig.key === 'manager' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                            </th>
                                            <th onClick={() => requestSort('statut')} style={{cursor: 'pointer', userSelect: 'none'}}>
                                                Statut {sortConfig.key === 'statut' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                            </th>
                                            {/* NOUVELLE COLONNE ICI */}
                                            <th onClick={() => requestSort('date_emission')} style={{cursor: 'pointer', userSelect: 'none'}}>
                                                Mois / Année {sortConfig.key === 'date_emission' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                            </th>
                                            <th onClick={() => requestSort('date_echeance')} style={{cursor: 'pointer', userSelect: 'none'}}>
                                                Échéance {sortConfig.key === 'date_echeance' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                            </th>
                                            <th onClick={() => requestSort('jours_retard')} style={{cursor: 'pointer', userSelect: 'none'}}>
                                                Retard {sortConfig.key === 'jours_retard' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                            </th>
                                            <th onClick={() => requestSort('montant_ttc')} style={{cursor: 'pointer', userSelect: 'none', textAlign: 'right'}}>
                                                Montant TTC {sortConfig.key === 'montant_ttc' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAndSortedDetails.map(inv => (
                                            <tr key={inv.id}>
                                                <td style={{ fontWeight: '600' }}>{inv.client}</td>
                                                <td>{inv.consultant}</td>
                                                <td style={{ color: '#64748b', fontSize: '0.9rem' }}>{inv.manager || '-'}</td>
                                                <td>
                                                    <span className={`${styles.badge} ${
                                                        inv.statut === 'Relance 2' ? styles.badgeRed : 
                                                        inv.statut === 'Relance 1' ? styles.badgeOrange : styles.badgeGray
                                                    }`}>
                                                        {inv.statut}
                                                    </span>
                                                </td>
                                                {/* NOUVELLE DONNÉE : On transforme 'YYYY-MM-DD' en 'MM/YYYY' */}
                                                <td style={{ fontWeight: '500', color: '#475569' }}>
                                                    {inv.date_emission ? `${inv.date_emission.split('-')[1]}/${inv.date_emission.split('-')[0]}` : '-'}
                                                </td>
                                                <td>{inv.date_echeance}</td>
                                                <td style={{ color: inv.jours_retard > 0 ? '#ef4444' : '#10b981', fontWeight: 'bold' }}>
                                                    {inv.jours_retard > 0 ? `+${inv.jours_retard} j` : `${inv.jours_retard} j`}
                                                </td>
                                                <td style={{textAlign: 'right', fontWeight: 'bold'}}>{formatCurrency(inv.montant_ttc)}</td>
                                            </tr>
                                        ))}
                                        {filteredAndSortedDetails.length === 0 && (
                                            <tr><td colSpan="8" style={{textAlign: 'center'}}>Aucune facture correspondante.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;